// client/src/pages/AdminHome.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import './AdminHome.css';
import logo from '../assets/chitconnect-logo-blue.png';
import { apiFetch } from '../lib/api';
import ApproveOrganiserList from '../components/ApproveOrganiserList';

// ✅ Centralized fetch wrapper using apiFetch (no base URLs here)
const fetchJSON = async (path, options = {}) => {
  const res = await apiFetch(path, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
};

// ✅ Custom hook must be defined BEFORE it's used
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

export default function AdminHome({ user }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);

  // initial loads (protected)
  useEffect(() => {
    (async () => {
      try {
        const [usersData, groupsData] = await Promise.all([
          fetchJSON('/api/users'),
          fetchJSON('/api/groups'),
        ]);

        setUsers(usersData.filter(u => u.access_level !== 'superadmin' && u.is_active !== 0));
        setGroups(groupsData.filter(g => !g.is_deleted));
      } catch (e) {
        alert(`Failed to load admin data: ${e.message}`);
      }
    })();
  }, []);

  const openUserModal = (user) => setEditingUser(user);

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm("Are you sure you want to deactivate this user?")) return;
    try {
      await fetchJSON(`/api/users/deactivate/${userId}`, { method: 'POST' });
      alert("User deactivated successfully.");
      setUsers(prev => prev.filter(user => user.id !== userId));
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  const refreshGroups = async () => {
    try {
      const data = await fetchJSON('/api/groups');
      setGroups(data.filter(g => !g.is_deleted));
    } catch (e) {
      alert(`Failed to refresh groups: ${e.message}`);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Archive this group? Members will no longer see it.')) return;

    try {
      await fetchJSON(`/api/groups/delete/${groupId}`, { method: 'POST' }); // or DELETE if API supports
      alert('Group archived.');
      await refreshGroups();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  const handleSaveEdit = async (updatedUser) => {
    try {
      await fetchJSON(`/api/users/update/${updatedUser.id}`, {
        method: 'POST',
        body: JSON.stringify(updatedUser),
      });
      alert('User updated successfully');
      setEditingUser(null);

      const data = await fetchJSON('/api/users');
      setUsers(data.filter(u => u.access_level !== 'superadmin' && u.is_active !== 0));
    } catch (e) {
      alert(`Update failed: ${e.message}`);
    }
  };

  const handleApproveGroup = async (groupId) => {
    if (!window.confirm("Are you sure you want to approve this group?")) return;

    try {
      await fetchJSON(`/api/groups/approve/${groupId}`, {
        method: 'POST',
        body: JSON.stringify({ approved: true }),
      });
      alert("Group approved.");
      await refreshGroups();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  const filteredUsers = users.filter(u => {
    const lower = searchTerm.toLowerCase();
    return (
      u.firstName?.toLowerCase().includes(lower) ||
      u.lastName?.toLowerCase().includes(lower) ||
      u.email?.toLowerCase().includes(lower) ||
      u.mobile?.includes(lower)
    );
  });

  const formatDate = (dateString) => {
    try { return new Date(dateString).toISOString().split('T')[0]; }
    catch { return dateString; }
  };

  return (
    <div className="main-content user-home">
      <div className="home-container">
        <div className="card">
          <div className="home-header">
            <img src={logo} alt="ChitConnect" className="register-logo" />
            <h1 style={{ color: '#0A3E75' }}>Welcome Admin</h1>
          </div>

          {/* ✅ New: Pending organiser requests (superadmin view) */}
          <ApproveOrganiserList />

          <h2 style={{ marginTop: '20px' }}>Active Users</h2>
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="user-search"
          />

          {isMobile ? (
            <div className="user-card-container">
              {filteredUsers.map(u => (
                <div className="user-card" key={u.id}>
                  <h3>{u.firstName} {u.lastName}</h3>
                  <p><strong>Email:</strong> {u.email}</p>
                  <p><strong>Mobile:</strong> {u.mobile}</p>
                  <p><strong>Role:</strong> {u.access_level || 'N/A'}</p>
                  <div className="button-group">
                    <button className="action-button" onClick={() => openUserModal(u)}>Edit</button>
                    <button className="action-button" onClick={() => handleDeactivateUser(u.id)}>Deactivate</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className="group-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.id}>
                    <td>{u.firstName} {u.lastName}</td>
                    <td>{u.email}</td>
                    <td>{u.mobile}</td>
                    <td>{u.access_level}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="action-button" onClick={() => openUserModal(u)}>Edit</button>
                        <button className="action-button" onClick={() => handleDeactivateUser(u.id)}>Deactivate</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h2 style={{ marginTop: '40px' }}>All Groups</h2>
          {groups.length > 0 ? (
            isMobile ? (
              <div className="group-card-list">
                {groups.map(g => (
                  <div className="group-card" key={g.id}>
                    <h4>{g.name}</h4>
                    <p><strong>Start Date:</strong> {formatDate(g.auction_start_date)}</p>
                    <p><strong>Duration:</strong> {g.duration_months} months</p>
                    <p><strong>Monthly:</strong> ${g.monthly_contribution}</p>
                    <p><strong>Status:</strong> {g.status}</p>

                    {/* Superadmin convenience links */}
                    <div className="button-group" style={{ marginTop: 10 }}>
                      <button
                        className="action-button"
                        onClick={() => navigate(`/groups/${g.id}`)}
                      >
                        View Group
                      </button>
                      <button
                        className="action-button"
                        onClick={() => navigate(`/groups/${g.id}/members`)}
                      >
                        View Members
                      </button>
                    </div>

                    <button
                      className="action-button"
                      onClick={() => navigate(`/groups/${g.id}/edit`)}
                    >
                      Admin Edit
                    </button>

                    {g.status === 'pending' && (
                      <button className="action-button" onClick={() => handleApproveGroup(g.id)}>
                        Approve
                      </button>
                    )}
                    <button
                      className="action-button danger-button"
                      onClick={() => handleDeleteGroup(g.id)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <table className="group-table">
                <thead>
                  <tr>
                    <th>Group Name</th>
                    <th>Status</th>
                    <th>Start Date</th>
                    <th>Duration</th>
                    <th>Monthly Amount</th>
                    <th>Actions</th> 
                  </tr>
                </thead>
                <tbody>
                  {groups.map(g => (
                    <tr key={g.id}>
                      <td>{g.name}</td>
                      <td>{g.status}</td>
                      <td>{formatDate(g.auction_start_date)}</td>
                      <td>{g.duration} {g.contribution_frequency || 'months'}</td>
                      <td>${g.contribution}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="action-button"
                            onClick={() => navigate(`/groups/${g.id}`)}
                          >
                            View
                          </button>
                          <button
                            className="action-button"
                            onClick={() => navigate(`/groups/${g.id}/members`)}
                          >
                            Members
                          </button>
                          <button
                            className="action-button"
                            onClick={() => navigate(`/groups/${g.id}/edit`)}
                          >
                            Admin Edit
                          </button>
                          {g.status === 'pending' ? (
                            <button
                              className="action-button"
                              onClick={() => handleApproveGroup(g.id)}
                            >
                              Approve
                            </button>
                          ) : (
                            <span style={{ color: 'green', fontWeight: 'bold' }}>Approved</span>
                          )}
                          <button
                            className="action-button danger-button"
                            onClick={() => handleDeleteGroup(g.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            <p>No groups available.</p>
          )}
        </div>
      </div>

      {editingUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit User</h3>
            <input
              type="text"
              value={editingUser.firstName}
              onChange={(e) =>
                setEditingUser({ ...editingUser, firstName: e.target.value })
              }
              placeholder="First Name"
            />
            <input
              type="text"
              value={editingUser.lastName}
              onChange={(e) =>
                setEditingUser({ ...editingUser, lastName: e.target.value })
              }
              placeholder="Last Name"
            />
            <input
              type="email"
              value={editingUser.email}
              onChange={(e) =>
                setEditingUser({ ...editingUser, email: e.target.value })
              }
              placeholder="Email"
            />
            <input
              type="text"
              value={editingUser.mobile}
              onChange={(e) =>
                setEditingUser({ ...editingUser, mobile: e.target.value })
              }
              placeholder="Mobile"
            />
            <div className="modal-actions">
              <button onClick={() => handleSaveEdit(editingUser)} className="action-button">
                Save
              </button>
              <button onClick={() => setEditingUser(null)} className="action-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
